import { QaReportsViewModel } from "../../viewmodels/qaReports/QaReportsViewModel";

import { connect } from "react-redux";
import { Navigate, useParams } from "react-router-dom";

import * as actions from "../../store/actions/index";

import { classNames, withRouter } from "../../shared/utility";

import EmptyModal from "../../components/modals/EmptyModal";
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
import SelectMenu from "../../components/controls/SelectMenu";
import QaReceivingOptions from "../../components/forms/qaReports/receiving/QaReceivingOptions";
import { useEffect, useRef, useState } from "react";
import SalesByPluFilters from "../../components/forms/qaReports/salesByPlu/SalesByPluFilters";
import FlowReportsFilters from "../../components/forms/qaReports/flowReports/FlowReportsFilters";
import ProductionDetailsFilter from "../../components/forms/qaReports/productionDetails/ProductionDetailsFilter";
import ProductionSummaryFilter from "../../components/forms/qaReports/productionSummary/ProductionSummaryFilter";
import SalesComparisonReportFilter from "../../components/forms/qaReports/salesComparison/SalesComparisonReportFilter";
import Input from "../../components/controls/Input";
import Button from "../../components/controls/Button";
import ReportDescription from "../../components/forms/qaReports/ReportDescription";
import ReportLotNumberLabel from "../../components/forms/qaReports/ReportLotNumberLabel";
import QaReportsLoadingSkeleton from "../../components/forms/qaReports/QaReportsLoadingSkeleton";
import SalesPeriodDateRange from "../../components/forms/qaReports/salesComparison/SalesPeriodDateRange";
import CustomerProfitabilityFilters from "../../components/forms/qaReports/finance/CustomerProfitabilityFilters";
import ProductionCostYieldFilters from "../../components/forms/qaReports/finance/ProductionCostYieldFilters";
import FieldCostsFilters from "../../components/forms/qaReports/finance/FieldCostsFilters";
import DataQualityFilters from "../../components/forms/qaReports/finance/DataQualityFilters";
import {
  filterQaReportOptions,
  getQaReportCategory,
  isQaReportCategoryKey,
  resolveQaReportCategoryKey,
} from "../../shared/qaReportCategories";
import {
  getMissingQaReportOptionData,
  QA_REPORT_IDS,
} from "../../shared/qaReportOptions";

function QaReports(props) {
  const { logout, reportCategoryOverride } = props;
  const { reportCategory: reportCategoryParam } = useParams();
  const [optionsLoading, setOptionsLoading] = useState(false);
  const loadedOptionData = useRef(new Set());
  const {
    emptyModal,
    setEmptyModelOpen,
    filters,
    printSelectedReport,
    qaReportOptions,
    setFilters,
    reportType,
    setReportType,
    resetFilters,
    resetReportSelection,
    categories,
    categoryLoading,
    customers,
    products,
    rooms,
    workOrders,
    roomEntityLabel,
    wipsEntityLabel,
    suppliers,
    getCustomersForDropdown,
    getProductOptionsForDropdown,
    getRoomsForDropdown,
    getWorkOrdersForDropdown,
    getSupplierOptionsForDropdown,
    classes,
    getClassTypes,
    weighingUsers,
    getWeighingUsersForDropdown,
  } = QaReportsViewModel(logout);

  const effectiveReportCategory = reportCategoryOverride ?? reportCategoryParam;
  const reportCategory = resolveQaReportCategoryKey(
    reportCategoryParam,
    reportCategoryOverride,
  );
  const category = getQaReportCategory(reportCategory);
  const categoryReportOptions = filterQaReportOptions(
    qaReportOptions,
    reportCategory,
  );

  useEffect(() => {
    loadedOptionData.current.delete("workOrders");
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    const missingOptionData = getMissingQaReportOptionData(
      reportType.value,
      loadedOptionData.current,
    );

    if (missingOptionData.length === 0) {
      setOptionsLoading(false);
      return;
    }

    const optionLoaders = {
      classes: getClassTypes,
      customers: getCustomersForDropdown,
      products: getProductOptionsForDropdown,
      rooms: getRoomsForDropdown,
      suppliers: getSupplierOptionsForDropdown,
      workOrders: getWorkOrdersForDropdown,
      weighingUsers: getWeighingUsersForDropdown,
    };
    let isCurrentSelection = true;

    setOptionsLoading(true);
    Promise.all(
      missingOptionData.map(async (optionData) => {
        const loaded = await optionLoaders[optionData]();
        if (loaded) {
          loadedOptionData.current.add(optionData);
        }
      }),
    ).finally(() => {
      if (isCurrentSelection) {
        setOptionsLoading(false);
      }
    });

    return () => {
      isCurrentSelection = false;
    };
  }, [
    getClassTypes,
    getCustomersForDropdown,
    getProductOptionsForDropdown,
    getRoomsForDropdown,
    getWorkOrdersForDropdown,
    getSupplierOptionsForDropdown,
    getWeighingUsersForDropdown,
    reportType.value,
  ]);

  useEffect(() => {
    resetReportSelection();
  }, [reportCategory, resetReportSelection]);

  if (!isQaReportCategoryKey(effectiveReportCategory)) {
    return <Navigate replace to="/qa-reports/operations" />;
  }

  function renderReportTypeFields() {
    switch (reportType.value) {
      case QA_REPORT_IDS.receivingDetails:
        return (
          <div>
            <QaReceivingOptions
              filters={filters}
              setFilters={setFilters}
              categories={categories}
              products={products}
              classes={classes}
              suppliers={suppliers}
            />
          </div>
        );

      case QA_REPORT_IDS.receivingSummary:
        return (
          <div>
            <QaReceivingOptions
              filters={filters}
              setFilters={setFilters}
              categories={categories}
              products={products}
              classes={classes}
              suppliers={suppliers}
            />
          </div>
        );

      case QA_REPORT_IDS.salesByPlu:
        return (
          <div>
            <SalesByPluFilters
              filters={filters}
              setFilters={setFilters}
              products={products}
              classes={classes}
              categories={categories}
            />
          </div>
        );

      case QA_REPORT_IDS.inventoryFlow:
        return (
          <div>
            <FlowReportsFilters
              filters={filters}
              setFilters={setFilters}
              products={products}
            />
          </div>
        );

      case QA_REPORT_IDS.productionDetail:
        return (
          <div>
            <ProductionDetailsFilter
              filters={filters}
              setFilters={setFilters}
              products={products}
            />
          </div>
        );

      case QA_REPORT_IDS.productionSummary:
        return (
          <div>
            <ProductionSummaryFilter
              filters={filters}
              setFilters={setFilters}
              products={products}
              classes={classes}
              categories={categories}
            />
          </div>
        );
      case QA_REPORT_IDS.salesComparison:
        return (
          <div>
            <SalesComparisonReportFilter
              filters={filters}
              setFilters={setFilters}
              classes={classes}
              categories={categories}
            />
          </div>
        );

      case QA_REPORT_IDS.employeeWeighing:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
            <SelectMenu
              title="Weighing user"
              handleInputChange={(e) =>
                setFilters({ ...filters, weighting_user_id: e.target.value })
              }
              name="weighting_user_id"
              options={[
                { value: -1, label: "Select weighing user" },
                ...weighingUsers.filter((o) => o.value !== -1),
              ]}
              value={filters.weighting_user_id}
            />
            <Input
              label={
                <ReportLotNumberLabel
                  report="employeeWeighing"
                  htmlFor="lot_number"
                />
              }
              name="lot_number"
              value={filters.lot_number}
              onChange={(e) =>
                setFilters({ ...filters, lot_number: e.target.value })
              }
              labelOn={true}
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />
          </div>
        );

      case QA_REPORT_IDS.customerProfitability:
        return (
          <CustomerProfitabilityFilters
            filters={filters}
            setFilters={setFilters}
            customers={customers}
            products={products}
            classes={classes}
            categories={categories}
          />
        );

      case QA_REPORT_IDS.productionCostYield:
        return (
          <ProductionCostYieldFilters
            filters={filters}
            setFilters={setFilters}
            products={products}
            classes={classes}
            categories={categories}
            rooms={rooms}
            roomEntityLabel={roomEntityLabel}
          />
        );

      case QA_REPORT_IDS.fieldCosts:
        return (
          <FieldCostsFilters
            filters={filters}
            setFilters={setFilters}
            rooms={rooms}
            roomEntityLabel={roomEntityLabel}
            wipsEntityLabel={wipsEntityLabel}
            workOrders={workOrders}
          />
        );

      case QA_REPORT_IDS.financeDataQuality:
        return (
          <DataQualityFilters
            filters={filters}
            setFilters={setFilters}
            products={products}
            classes={classes}
            categories={categories}
          />
        );

      default:
        return <div></div>;
    }
  }

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div
            aria-busy={optionsLoading || categoryLoading}
            className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom"
          >
            {optionsLoading || categoryLoading ? (
              <div className="w-full">
                <QaReportsLoadingSkeleton />
              </div>
            ) : (
              <div className="sm:flex-auto space-y-5">
                <h1 className="text-base font-semibold leading-6 text-brand">
                  {category.name}
                </h1>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                  <div className="mt-2 sm:mt-0">
                    <SelectMenu
                      title="Report type"
                      handleInputChange={(e) => {
                        const missingOptionData = getMissingQaReportOptionData(
                          e.target.value.value,
                          loadedOptionData.current,
                        );
                        setOptionsLoading(missingOptionData.length > 0);
                        setReportType(e.target.value);
                        resetFilters();
                      }}
                      name="reportType"
                      options={categoryReportOptions}
                      value={reportType}
                    />
                  </div>
                  {reportType.value === QA_REPORT_IDS.salesComparison ? (
                    <SalesPeriodDateRange
                      period={1}
                      className="sm:col-span-2"
                      startDate={filters.startDate}
                      endDate={filters.endDate}
                      onChange={(name, value) =>
                        setFilters({
                          ...filters,
                          [name]: value,
                        })
                      }
                      resetFilter={() =>
                        setFilters({
                          ...filters,
                          startDate: null,
                          endDate: null,
                        })
                      }
                      maxDate={new Date()}
                    />
                  ) : (
                    <div
                      className={classNames(
                        reportType.value === QA_REPORT_IDS.openWips
                          ? "hidden"
                          : "col-span-2",
                      )}
                    >
                      <DateRangeFilter
                        hideTitle={true}
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        onChange={(name, value) =>
                          setFilters({
                            ...filters,
                            [name]: value,
                            ...(reportType.value === QA_REPORT_IDS.fieldCosts
                              ? { work_orders: [] }
                              : {}),
                          })
                        }
                        resetFilter={() =>
                          setFilters({
                            ...filters,
                            startDate: null,
                            endDate: null,
                            ...(reportType.value === QA_REPORT_IDS.fieldCosts
                              ? { work_orders: [] }
                              : {}),
                          })
                        }
                        maxDate={new Date()}
                        showOneDate={
                          reportType.value === QA_REPORT_IDS.inventoryValuation
                        }
                      />
                    </div>
                  )}
                  <div className="w-full sm:w-auto">
                    <Button
                      className="w-full sm:w-auto"
                      onClick={printSelectedReport}
                    >
                      Download
                    </Button>
                  </div>
                </div>
                <ReportDescription report={reportType} />
                {renderReportTypeFields()}
              </div>
            )}
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(QaReports),
);
